import type { OnboardingV2State, OnboardingWorkCoverageKind } from '../../store/onboardingV2/types.ts';
import {
  COMMERCE_DELIVERY_RADIUS_KM_MIN,
  COMMERCE_DELIVERY_RADIUS_UI_KM_MAX,
} from '../../services/commerceService.ts';

export type JobBoardWorkCoverageKind = OnboardingWorkCoverageKind;

export type OnboardingJobBoardCoverageSnapshot = Pick<
  OnboardingV2State,
  | 'allCountry'
  | 'workCoverageKind'
  | 'workZones'
  | 'influenceLat'
  | 'influenceLng'
  | 'influenceRadiusKm'
  | 'influencePlaceLabel'
>;

export const DEFAULT_INFLUENCE_RADIUS_KM = 50;

export function inferOnboardingJobBoardCoverageKind(
  state: Pick<OnboardingV2State, 'allCountry' | 'workCoverageKind'>
): JobBoardWorkCoverageKind {
  if (state.allCountry) return 'allCountry';
  const k = state.workCoverageKind ?? 'specificZones';
  if (k === 'mapRadius') return 'mapRadius';
  return 'specificZones';
}

export function onboardingJobBoardSnapshotAllCountry(): OnboardingJobBoardCoverageSnapshot {
  return {
    allCountry: true,
    workCoverageKind: 'allCountry',
    workZones: [],
    influenceLat: null,
    influenceLng: null,
    influenceRadiusKm: DEFAULT_INFLUENCE_RADIUS_KM,
    influencePlaceLabel: null,
  };
}

export function onboardingJobBoardSnapshotSpecificZones(): Pick<
  OnboardingV2State,
  'allCountry' | 'workCoverageKind'
> {
  return {
    allCountry: false,
    workCoverageKind: 'specificZones',
  };
}

export function onboardingJobBoardSnapshotMapRadius(
  previousRadiusKm: number | null | undefined
): OnboardingJobBoardCoverageSnapshot {
  const raw = previousRadiusKm ?? DEFAULT_INFLUENCE_RADIUS_KM;
  const r = Number.isFinite(raw)
    ? Math.min(
        COMMERCE_DELIVERY_RADIUS_UI_KM_MAX,
        Math.max(COMMERCE_DELIVERY_RADIUS_KM_MIN, Math.round(Number(raw)))
      )
    : DEFAULT_INFLUENCE_RADIUS_KM;
  return {
    allCountry: false,
    workCoverageKind: 'mapRadius',
    workZones: [],
    influenceLat: null,
    influenceLng: null,
    influenceRadiusKm: r,
    influencePlaceLabel: null,
  };
}

export function mergeOnboardingJobBoardCoverage<T extends OnboardingV2State>(
  state: T,
  patch: Partial<OnboardingJobBoardCoverageSnapshot> | Pick<OnboardingV2State, 'allCountry' | 'workCoverageKind'>
): T {
  return { ...state, ...patch };
}

export function isOnboardingJobBoardCoverageComplete(
  state: Pick<OnboardingV2State, 'allCountry' | 'workZones'>
): boolean {
  if (state.allCountry) return true;
  return state.workZones.length > 0;
}

export function isOnboardingMapCoverageValidForSubmit(
  state: Pick<
    OnboardingV2State,
    'workCoverageKind' | 'influenceLat' | 'influenceLng' | 'influenceRadiusKm'
  >
): boolean {
  const coverageKind = state.workCoverageKind ?? 'specificZones';
  const infLat = state.influenceLat;
  const infLng = state.influenceLng;
  const infKm = state.influenceRadiusKm;
  return (
    coverageKind === 'mapRadius' &&
    infLat != null &&
    infLng != null &&
    Number.isFinite(infLat) &&
    Number.isFinite(infLng) &&
    Number.isFinite(infKm) &&
    infKm >= COMMERCE_DELIVERY_RADIUS_KM_MIN &&
    infKm <= COMMERCE_DELIVERY_RADIUS_UI_KM_MAX
  );
}

export type ProfileJobBoardCoverageFormValues = {
  allCountry: boolean;
  jobBoardZones: unknown[];
  workCoverageKind: JobBoardWorkCoverageKind;
  influenceLat: number | null;
  influenceLng: number | null;
  influenceRadiusKm: number;
};

export function profileJobBoardApplyAllCountryMode(): ProfileJobBoardCoverageFormValues {
  return {
    allCountry: true,
    jobBoardZones: [],
    workCoverageKind: 'allCountry',
    influenceLat: null,
    influenceLng: null,
    influenceRadiusKm: DEFAULT_INFLUENCE_RADIUS_KM,
  };
}

export function profileJobBoardApplyListedZonesMode(): Pick<
  ProfileJobBoardCoverageFormValues,
  'allCountry' | 'workCoverageKind' | 'influenceLat' | 'influenceLng'
> {
  return {
    allCountry: false,
    workCoverageKind: 'specificZones',
    influenceLat: null,
    influenceLng: null,
  };
}

export function profileJobBoardApplyMapRadiusMode(
  previousRadiusKm: number | null | undefined,
  restoreCoords?: { influenceLat: number | null; influenceLng: number | null } | null
): ProfileJobBoardCoverageFormValues {
  const snap = onboardingJobBoardSnapshotMapRadius(previousRadiusKm);
  const lat = restoreCoords?.influenceLat;
  const lng = restoreCoords?.influenceLng;
  const useRestore =
    lat != null &&
    lng != null &&
    Number.isFinite(Number(lat)) &&
    Number.isFinite(Number(lng));
  return {
    allCountry: false,
    jobBoardZones: [],
    workCoverageKind: 'mapRadius',
    influenceLat: useRestore ? Number(lat) : snap.influenceLat,
    influenceLng: useRestore ? Number(lng) : snap.influenceLng,
    influenceRadiusKm: snap.influenceRadiusKm,
  };
}

export function buildInitialProfileJobBoardCoverage(userData: {
  jobBoardZones?: unknown[];
  influenceGeo?: {
    latitude?: unknown;
    longitude?: unknown;
    influenceRadiusKm?: unknown;
  };
  latitude?: unknown;
  longitude?: unknown;
  influenceRadiusKm?: unknown;
}): ProfileJobBoardCoverageFormValues & { jobBoardZones: unknown[] } {
  const zones = Array.isArray(userData?.jobBoardZones) ? [...(userData.jobBoardZones as unknown[])] : [];
  const hasAllCountryMarker = zones.some((z: { allCountry?: boolean }) => z?.allCountry === true);
  if (hasAllCountryMarker) {
    return { ...profileJobBoardApplyAllCountryMode(), jobBoardZones: [] };
  }
  const ig = userData?.influenceGeo;
  const latRaw = ig?.latitude ?? userData?.latitude;
  const lngRaw = ig?.longitude ?? userData?.longitude;
  const lat = latRaw != null && Number.isFinite(Number(latRaw)) ? Number(latRaw) : null;
  const lng = lngRaw != null && Number.isFinite(Number(lngRaw)) ? Number(lngRaw) : null;
  const rkRaw = ig?.influenceRadiusKm ?? userData?.influenceRadiusKm;
  const rk =
    rkRaw != null && Number.isFinite(Number(rkRaw))
      ? Math.min(
          COMMERCE_DELIVERY_RADIUS_UI_KM_MAX,
          Math.max(COMMERCE_DELIVERY_RADIUS_KM_MIN, Math.round(Number(rkRaw)))
        )
      : DEFAULT_INFLUENCE_RADIUS_KM;

  if (lat != null && lng != null) {
    return {
      allCountry: false,
      workCoverageKind: 'mapRadius',
      jobBoardZones: [],
      influenceLat: lat,
      influenceLng: lng,
      influenceRadiusKm: rk,
    };
  }

  const listed = zones.filter((z: { allCountry?: boolean }) => !z?.allCountry);
  return {
    allCountry: false,
    workCoverageKind: 'specificZones',
    jobBoardZones: listed,
    influenceLat: null,
    influenceLng: null,
    influenceRadiusKm: DEFAULT_INFLUENCE_RADIUS_KM,
  };
}

export function isProfileJobBoardSaveReady(values: {
  workCoverageKind?: JobBoardWorkCoverageKind;
  allCountry?: boolean;
  jobBoardZones?: unknown[];
  influenceLat?: number | null;
  influenceLng?: number | null;
  influenceRadiusKm?: number;
}): boolean {
  const k =
    values.workCoverageKind ??
    (values.allCountry ? 'allCountry' : ('specificZones' as JobBoardWorkCoverageKind));
  if (k === 'allCountry') return true;
  if (k === 'mapRadius') {
    return isOnboardingMapCoverageValidForSubmit({
      workCoverageKind: 'mapRadius',
      influenceLat: values.influenceLat ?? null,
      influenceLng: values.influenceLng ?? null,
      influenceRadiusKm: values.influenceRadiusKm ?? DEFAULT_INFLUENCE_RADIUS_KM,
    });
  }
  const listed = Array.isArray(values.jobBoardZones)
    ? values.jobBoardZones.filter((z: { allCountry?: boolean }) => !z?.allCountry)
    : [];
  return listed.length > 0;
}
